import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent
} from "@mui/material";
import MainCard from "ui-component/cards/MainCard";
import PropertyApi from "../../../apis/property.api";

const propertiesApi = new PropertyApi();

/* ---------------- HELPERS ---------------- */

const formatType = (value = "") =>
  value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const InfoCard = ({ label, value }) => (
  <Card
    sx={{
      p: 2,
      borderRadius: 3,
      textAlign: "center",
      boxShadow: 1,
      height: "100%"
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>

    <Typography variant="h6" fontWeight={700}>
      {value ?? "-"}
    </Typography>
  </Card>
);

/* ---------------- RATING DOTS ---------------- */

const getRatingColor = (rating) => {
  if (rating < 1.5) return "#e53935";
  if (rating < 2.5) return "#fb8c00";
  if (rating < 3.5) return "#fdd835";
  if (rating < 4.5) return "#7cb342";
  return "#2e7d32";
};

const FiveCircleRating = ({ value = 0, size = 14 }) => {
  const filled = Math.round(value);
  const color = getRatingColor(value);

  return (
    <Stack direction="row" spacing={0.6} justifyContent="center">
      {Array.from({ length: 5 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            bgcolor: i < filled ? color : "grey.300"
          }}
        />
      ))}
    </Stack>
  );
};

export default function PropertyDetails() {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedStayPayments, setSelectedStayPayments] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      const res = await propertiesApi.getPropertyDetails(id);
      setData(res?.data?.data);
    };

    fetchDetails();
  }, [id]);

  if (!data) return null;

  const reviews = data.propertyRatingReviews || [];
  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, 3);

  // ---------------- GROUP PAYMENTS BY TENANT STAY ----------------

  const paymentsByStay = (data.payments || []).reduce((acc, payment) => {
    const key = payment.tenantStayId;

    if (!acc[key]) {
      acc[key] = {
        tenant: payment.tenant,
        payments: []
      };
    }

    acc[key].payments.push(payment);
    return acc;
  }, {});

  const openPaymentsModal = (stayId, tenant) => {
    const payments = paymentsByStay[stayId]?.payments || [];

    setSelectedStayPayments(payments);
    setSelectedTenant(tenant);
    setPaymentModalOpen(true);
  };

  return (
    <Grid container spacing={3}>
      {/* ================= HERO HEADER ================= */}

      <Grid item xs={12}>
        <Card
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            position: "relative"
          }}
        >
          <Box
            component="img"
            src={data.images?.[0]}
            sx={{
              width: "100%",
              height: 340,
              objectFit: "cover",
              filter: "brightness(60%)" // 👈 reduce image visibility
            }}
          />

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              p: 3,
              background:
                "linear-gradient(transparent, rgba(0,0,0,0.85))", // 👈 darker overlay
              color: "#fff"
            }}
          >
            <Typography color={"white"} variant="h4" fontWeight={700}>
              {data.name}
            </Typography>

            <Typography color={"white"} variant="body2">
              {data.address}, {data.city}
            </Typography>

            <br />

            <Typography color={"white"} variant="body2">
              {data.description}
            </Typography>

            <br />

            <Stack direction="row" spacing={1} mt={1}>
              <Chip label={formatType(data.type)} color="primary" />
              <Chip label={data.furnishing} color="primary" />
              <Chip label={data.condition} color="primary" />
              <Chip label={data.status.toUpperCase()} color="primary" />
            </Stack>
          </Box>
        </Card>
      </Grid>

      {/* ================= IMAGE GALLERY ================= */}

      <Grid item xs={12}>
        <MainCard title="Property Gallery">
          <Grid container spacing={2}>
            {data.images?.map((img, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Box
                  component="img"
                  src={img}
                  sx={{
                    width: "100%",
                    height: 180,
                    borderRadius: 2,
                    objectFit: "cover",
                    transition: "0.3s",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.05)"
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </MainCard>
      </Grid>

      {/* ================= PROPERTY INFO ================= */}

      <Grid item xs={12}>
        <MainCard title="Property Highlights">
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <InfoCard label="Rent" value={`₹${data.rent}`} />
            </Grid>

            <Grid item xs={6} md={3}>
              <InfoCard label="Deposit" value={`₹${data.securityDeposit}`} />
            </Grid>

            <Grid item xs={6} md={3}>
              <InfoCard label="Rooms" value={data.rooms} />
            </Grid>

            <Grid item xs={6} md={3}>
              <InfoCard label="Size" value={data.sizeV2} />
            </Grid>

            <Grid item xs={6} md={3}>
              <InfoCard label="Bedrooms" value={data.bed_rooms} />
            </Grid>

            <Grid item xs={6} md={3}>
              <InfoCard label="Bathrooms" value={data.baths} />
            </Grid>

            <Grid item xs={6} md={3}>
              <InfoCard label="Kitchen" value={data.kitchen} />
            </Grid>

            <Grid item xs={6} md={3}>
              <InfoCard label="Living Rooms" value={data.living_rooms} />
            </Grid>
          </Grid>
        </MainCard>
      </Grid>

      {/* ================= OWNER ================= */}

      <Grid item xs={12} md={6}>
        <MainCard title="Owner Information">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 60, height: 60 }}>
              {data.owner.fullName.charAt(0)}
            </Avatar>

            <Box>
              <Typography fontWeight={700}>
                {data.owner.fullName}
              </Typography>

              <Typography variant="body2">
                {data.owner.userId}
              </Typography>
            </Box>
          </Stack>
        </MainCard>
      </Grid>

      {/* ================= TENANTS ================= */}

      <Grid item xs={12} md={6}>
        <MainCard title="Current Tenant">
          {data.currentTenants.length === 0 ? (
            <Typography>No active tenants</Typography>
          ) : (
            <Stack spacing={2}>
              {data.currentTenants.map((t) => (
                <Card key={t.tenantStayId} variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={2}>
                      <Avatar
                        src={t.profilePicture}
                        sx={{ width: 50, height: 50 }}
                      />

                      <Box>
                        <Typography fontWeight={600}>
                          {t.fullName}
                        </Typography>

                        <Typography variant="body2">
                          {t.email}
                        </Typography>

                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1 }}
                          onClick={() => openPaymentsModal(t.tenantStayId, t)}
                        >
                          View Payments
                        </Button>

                        <Typography variant="caption">
                          Since{" "}
                          {new Date(t.startDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </MainCard>
      </Grid>

      {/* ================= PREVIOUS TENANTS ================= */}

      <Grid item xs={12}>
        <MainCard title="Previous Tenants">

          {data.previousTenants?.length === 0 ? (
            <Typography>No previous tenants</Typography>
          ) : (

            <Grid container spacing={2}>

              {data.previousTenants.map((t) => (

                <Grid item xs={12} md={6} lg={4} key={t.tenantStayId}>

                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      transition: "0.2s",
                      "&:hover": {
                        boxShadow: 4
                      }
                    }}
                  >

                    <CardContent>

                      <Stack direction="row" spacing={2} alignItems="center">

                        <Avatar
                          src={t.profilePicture}
                          sx={{ width: 50, height: 50 }}
                        />

                        <Box>

                          <Typography fontWeight={600}>
                            {t.fullName}
                          </Typography>

                          <Typography variant="body2">
                            {t.email}
                          </Typography>

                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ mt: 1 }}
                            onClick={() => openPaymentsModal(t.tenantStayId, t)}
                          >
                            View Payments
                          </Button>

                        </Box>

                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack spacing={0.5}>

                        <Typography variant="caption">
                          <b>Stay:</b>{" "}
                          {new Date(t.startDate).toLocaleDateString()}
                          {" - "}
                          {new Date(t.endDate).toLocaleDateString()}
                        </Typography>

                        <Typography variant="caption">
                          <b>Rent:</b> ₹{t.donePrice}
                        </Typography>

                        <Typography variant="caption">
                          <b>Deposit:</b> ₹{t.securityDeposit}
                        </Typography>

                      </Stack>

                    </CardContent>

                  </Card>

                </Grid>

              ))}

            </Grid>

          )}

        </MainCard>
      </Grid>

      {/* ================= DOCUMENTS ================= */}

      <Grid item xs={12}>
        <MainCard title="Documents">
          <Grid container spacing={2}>
            {data.documents.map((doc) => (
              <Grid item xs={12} md={6} key={doc.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography fontWeight={600}>
                          {doc.name}
                        </Typography>

                        <Typography variant="caption">
                          {doc.type}
                        </Typography>
                      </Box>

                      <Button
                        size="small"
                        variant="contained"
                        href={doc.url}
                        target="_blank"
                      >
                        View
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </MainCard>
      </Grid>

      {/* ================= PAYMENTS ================= */}

      <Grid item xs={12}>
        <MainCard title="Property Payments">

          {Object.keys(paymentsByStay).length === 0 ? (
            <Typography>No payments found</Typography>
          ) : (

            <Stack spacing={3}>

              {Object.entries(paymentsByStay).map(([stayId, stayData]) => (

                <Card key={stayId} sx={{ borderRadius: 3 }}>

                  <CardContent>

                    {/* TENANT HEADER */}

                    <Stack direction="row" spacing={2} alignItems="center">

                      <Avatar src={stayData.tenant?.profilePicture} />

                      <Box>
                        <Typography fontWeight={600}>
                          {stayData.tenant?.fullName}
                        </Typography>

                        <Typography variant="caption">
                          {stayData.tenant?.email}
                        </Typography>
                      </Box>

                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {/* PAYMENT LIST */}

                    <Grid container spacing={2}>

                      {stayData.payments.map((p) => (

                        <Grid item xs={12} md={6} lg={4} key={p.paymentId}>

                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              p: 2
                            }}
                          >

                            <Stack spacing={1}>

                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography fontWeight={600}>
                                  ₹{p.amount}
                                </Typography>

                                <Chip
                                  label={p.paymentStatus}
                                  size="small"
                                  color={
                                    p.paymentStatus === "paid"
                                      ? "success"
                                      : "warning"
                                  }
                                />
                              </Stack>

                              <Typography variant="caption">
                                <b>Period:</b>{" "}
                                {new Date(p.startDate).toLocaleDateString()} -{" "}
                                {new Date(p.endDate).toLocaleDateString()}
                              </Typography>

                              <Typography variant="caption">
                                <b>Due:</b>{" "}
                                {new Date(p.dueDate).toLocaleDateString()}
                              </Typography>

                              <Typography variant="caption">
                                <b>Paid:</b> ₹{p.amountPaidSoFar}
                              </Typography>

                            </Stack>

                          </Card>

                        </Grid>

                      ))}

                    </Grid>

                  </CardContent>

                </Card>

              ))}

            </Stack>

          )}

        </MainCard>
      </Grid>

      {/* ================= RATINGS ================= */}

      <Grid item xs={12}>
        <MainCard title="Ratings">
          <Stack spacing={3} alignItems="center">
            <Typography variant="h3">
              {data.propertyRatings.avgOverallRating.toFixed(1)}
            </Typography>

            <FiveCircleRating
              value={data.propertyRatings.avgOverallRating}
              size={18}
            />

            <Typography variant="caption">
              {data.propertyRatings.totalReviews} reviews
            </Typography>

            <Divider sx={{ width: "100%" }} />

            <Grid container spacing={2}>
              {Object.entries(data.propertyRatings.avgRatings).map(
                ([key, val]) => (
                  <Grid item xs={6} md={2} key={key}>
                    <Card sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="caption">
                        {formatType(key)}
                      </Typography>

                      <FiveCircleRating value={val} />

                      <Typography fontWeight={600}>
                        {val.toFixed(1)}
                      </Typography>
                    </Card>
                  </Grid>
                )
              )}
            </Grid>
          </Stack>
        </MainCard>
      </Grid>

      {/* ================= REVIEWS ================= */}

      <Grid item xs={12}>
        <MainCard title="Tenant Reviews">
          <Stack spacing={2}>
            {visibleReviews.map((r) => (
              <Card key={r.ratingId}>
                <CardContent>
                  <Stack direction="row" spacing={2}>
                    <Avatar src={r.tenant.profilePicture} />

                    <Box>
                      <Typography fontWeight={600}>
                        {r.tenant.fullName}
                      </Typography>

                      <Typography variant="caption">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </Typography>

                      <Typography mt={1}>
                        {r.propertyComments || "No comment"}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}

            {reviews.length > 3 && (
              <Box textAlign="center">
                <Button
                  variant="outlined"
                  onClick={() =>
                    setShowAllReviews((p) => !p)
                  }
                >
                  {showAllReviews ? "Show Less" : "Show More"}
                </Button>
              </Box>
            )}
          </Stack>
        </MainCard>
      </Grid>


      {/* ================= PAYMENT MODAL ================= */}

      <Dialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Payments - {selectedTenant?.fullName}
        </DialogTitle>

        <DialogContent>

          {selectedStayPayments.length === 0 ? (
            <Typography>No payments found</Typography>
          ) : (

            <Grid container spacing={2} sx={{ mt: 1 }}>

              {selectedStayPayments.map((p) => (

                <Grid item xs={12} md={6} key={p.paymentId}>

                  <Card variant="outlined" sx={{ p: 2 }}>

                    <Stack spacing={1}>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                      >

                        <Typography fontWeight={600}>
                          ₹{p.amount}
                        </Typography>

                        <Chip
                          label={p.paymentStatus}
                          size="small"
                          color={
                            p.paymentStatus === "paid"
                              ? "success"
                              : "warning"
                          }
                        />

                      </Stack>

                      <Typography variant="caption">
                        <b>Period:</b>{" "}
                        {new Date(p.startDate).toLocaleDateString()} -
                        {new Date(p.endDate).toLocaleDateString()}
                      </Typography>

                      <Typography variant="caption">
                        <b>Due Date:</b>{" "}
                        {new Date(p.dueDate).toLocaleDateString()}
                      </Typography>

                      <Typography variant="caption">
                        <b>Paid:</b> ₹{p.amountPaidSoFar}
                      </Typography>

                    </Stack>

                  </Card>

                </Grid>

              ))}

            </Grid>

          )}

        </DialogContent>
      </Dialog>
    </Grid>
  );
}